import {
  Sequence,
  useCurrentFrame,
  useVideoConfig,
  spring,
  interpolate,
} from 'remotion';
import { useMemo } from 'react';

// Utils
import {
  createAnimationStyles,
  getNameLinesWithAnimation,
  formatOnboardingDate,
} from '@/utils';

// Fonts
import { lalezarFontFamily } from '@/styles/fonts';

// Types
import { AnimationPreset } from '@/types';

// Components
import { AnimatedText } from '@/components/common/AnimatedText';

// Constants
import { TEXT_ANIM, CLAMP_RIGHT } from '../constants';

// Theme
import { employeeYearInReviewTheme } from '@/components/EmployeeYearInReview/theme';

interface ProfileInfoProps {
  name: string;
  role: string;
  onboardingDate: string;
}

export const ProfileInfo = ({
  name,
  role,
  onboardingDate,
}: ProfileInfoProps) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const nameLinesConfig = useMemo(
    () =>
      getNameLinesWithAnimation(
        name,
        TEXT_ANIM.NAME_START,
        TEXT_ANIM.CHARACTER_STAGGER,
      ),
    [name],
  );

  const formattedDate = formatOnboardingDate(onboardingDate);

  // Role animation
  const roleSpring = spring({
    frame: frame - TEXT_ANIM.ROLE_START,
    fps,
    config: { damping: 12, stiffness: 100 },
  });

  const roleOpacity = interpolate(roleSpring, [0, 1], [0, 1], CLAMP_RIGHT);

  // Date label animation
  const dateLabelSpring = spring({
    frame: frame - TEXT_ANIM.DATE_LABEL_START,
    fps,
    config: { damping: 12, stiffness: 100 },
  });

  const dateLabelOpacity = interpolate(
    dateLabelSpring,
    [0, 1],
    [0, 1],
    CLAMP_RIGHT,
  );

  const { colors } = employeeYearInReviewTheme;

  return (
    <>
      {/* Name and Role */}
      <Sequence
        name="Name and Role"
        className="block text-end"
        style={{
          left: 75,
          top: 160,
          width: 400,
        }}
      >
        {/* Name - Character by character reveal */}
        <div className={`text-6xl ${colors.textPrimary} leading-tight mb-2.5`}>
          {nameLinesConfig.map(({ text, startFrame }) => (
            <div key={text}>
              <AnimatedText
                text={text}
                startFrame={startFrame}
                staggerFrames={TEXT_ANIM.CHARACTER_STAGGER}
                springPreset={AnimationPreset.GENTLE}
              />
            </div>
          ))}
        </div>

        {/* Role - Simple pop-in */}
        <div
          style={createAnimationStyles.popIn({
            scale: roleSpring,
            opacity: roleOpacity,
          })}
        >
          <h2
            className={`text-6xl leading-xl ${colors.textPrimary}`}
            style={{ fontFamily: lalezarFontFamily }}
          >
            {role}
          </h2>
        </div>
      </Sequence>

      {/* Onboarding Date */}
      <Sequence
        name="Onboarding Date"
        className="block text-end"
        style={{
          left: 75,
          top: 620,
          width: 400,
        }}
      >
        {/* Date Label - Pop-in */}
        <div
          style={createAnimationStyles.popIn({
            scale: dateLabelSpring,
            opacity: dateLabelOpacity,
          })}
        >
          <p className={`text-5xl ${colors.textPrimary} mb-1.5`}>On boarding</p>
        </div>

        {/* Date Value - Character by character reveal */}
        <p
          className={`text-6xl leading-xl ${colors.textPrimary}`}
          style={{ fontFamily: lalezarFontFamily }}
        >
          <AnimatedText
            text={formattedDate}
            startFrame={TEXT_ANIM.DATE_VALUE_START}
            staggerFrames={TEXT_ANIM.CHARACTER_STAGGER}
            springPreset={AnimationPreset.GENTLE}
          />
        </p>
      </Sequence>
    </>
  );
};
